import { createClient, AuthChangeEvent, Session } from '@supabase/supabase-js';

// Database types matching the backend
export interface Database {
  public: {
    Tables: {
      tasks: {
        Row: {
          id: string;
          title: string;
          description: string | null;
          quadrant: 'urgent-important' | 'important' | 'urgent' | 'neither';
          status: 'pending' | 'in-progress' | 'completed';
          voice_note_url: string | null;
          transcription: string | null;
          user_id: string;
          link: string | null;
          archived: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          title: string;
          description?: string | null;
          quadrant: 'urgent-important' | 'important' | 'urgent' | 'neither';
          status?: 'pending' | 'in-progress' | 'completed';
          voice_note_url?: string | null;
          transcription?: string | null;
          user_id: string;
          link?: string | null;
          archived?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          title?: string;
          description?: string | null;
          quadrant?: 'urgent-important' | 'important' | 'urgent' | 'neither';
          status?: 'pending' | 'in-progress' | 'completed';
          voice_note_url?: string | null;
          transcription?: string | null;
          user_id?: string;
          link?: string | null;
          archived?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      voice_uploads: {
        Row: {
          id: string;
          filename: string;
          original_name: string;
          file_path: string;
          mime_type: string;
          file_size: number;
          transcription_id: string | null;
          transcription_status: 'pending' | 'processing' | 'completed' | 'failed';
          transcription_text: string | null;
          title: string | null;
          user_id: string | null;
          error_message: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          filename: string;
          original_name: string;
          file_path: string;
          mime_type: string;
          file_size: number;
          transcription_id?: string | null;
          transcription_status?: 'pending' | 'processing' | 'completed' | 'failed';
          transcription_text?: string | null;
          title?: string | null;
          user_id?: string | null;
          error_message?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          filename?: string;
          original_name?: string;
          file_path?: string;
          mime_type?: string;
          file_size?: number;
          transcription_id?: string | null;
          transcription_status?: 'pending' | 'processing' | 'completed' | 'failed';
          transcription_text?: string | null;
          title?: string | null;
          user_id?: string | null;
          error_message?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      [_ in never]: never;
    };
    Enums: {
      [_ in never]: never;
    };
  };
}

// Supabase configuration
// Check if we're in test environment first (Jest sets NODE_ENV=test)
const isTestEnvironment = process.env.NODE_ENV === 'test' || 
                         typeof jest !== 'undefined' || 
                         process.env.JEST_WORKER_ID !== undefined;

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Use mock values for test environment, otherwise require real values
if (isTestEnvironment) {
  console.log('🧪 Running in test environment, using mock Supabase configuration');
} else {
  if (!supabaseUrl) {
    throw new Error('Missing REACT_APP_SUPABASE_URL environment variable');
  }
  if (!supabaseAnonKey) {
    throw new Error('Missing REACT_APP_SUPABASE_ANON_KEY environment variable');
  }
}

// Use mock values for test environment, real values for production
const finalSupabaseUrl = isTestEnvironment ? 'https://test.supabase.co' : supabaseUrl!;
const finalSupabaseAnonKey = isTestEnvironment ? 'test_key_for_jest_testing' : supabaseAnonKey!;

// Create Supabase client with simplified typing
export const supabase = createClient(
  finalSupabaseUrl,
  finalSupabaseAnonKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
    db: {
      schema: 'public',
    },
    global: {
      headers: {
        'X-Client-Info': 'narad-voice-ai-frontend',
      },
    },
  }
);

// Auth helper functions
export const auth = {
  // Sign up with email and password
  signUp: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    if (error) throw error;
    return data;
  },

  // Sign in with email and password
  signIn: async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    return data;
  },

  // Sign out
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },

  // Get current user
  getCurrentUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  },

  // Get current session
  getSession: async () => {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  },

  // Listen to auth changes
  onAuthStateChange: (callback: (event: AuthChangeEvent, session: Session | null) => void) => {
    return supabase.auth.onAuthStateChange(callback);
  },
};

// Database helper functions with simplified typing
export const db = {
  tasks: {
    // Get all tasks for current user
    getAll: async (options?: {
      quadrant?: string;
      status?: string;
      archived?: boolean;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      let query = (supabase as any)
        .from('tasks')
        .select('*');

      // Apply filters
      if (options?.quadrant) {
        query = query.eq('quadrant', options.quadrant);
      }
      
      if (options?.status) {
        query = query.eq('status', options.status);
      }
      
      if (options?.archived !== undefined) {
        query = query.eq('archived', options.archived);
      }
      
      if (options?.search) {
        query = query.or(`title.ilike.%${options.search}%,description.ilike.%${options.search}%`);
      }
      
      // Apply pagination
      if (options?.page && options?.limit) {
        const from = (options.page - 1) * options.limit;
        const to = from + options.limit - 1;
        query = query.range(from, to);
      }
      
      // Order by created_at descending
      query = query.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },

    // Get single task by ID
    getById: async (id: string) => {
      const { data, error } = await (supabase as any)
        .from('tasks')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },

    // Create new task
    create: async (task: any) => {
      const { data, error } = await (supabase as any)
        .from('tasks')
        .insert(task)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    // Update task
    update: async (id: string, updates: any) => {
      const { data, error } = await (supabase as any)
        .from('tasks')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },

    // Delete task
    delete: async (id: string) => {
      const { error } = await (supabase as any)
        .from('tasks')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },

    // Subscribe to task changes
    subscribe: (callback: (payload: any) => void) => {
      return (supabase as any)
        .channel('tasks')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tasks' }, callback)
        .subscribe();
    },
  },
};

// Export default
export default supabase;